import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ProductSliderProps {
  imagens: string[];
  highlightedImage?: string;
  handleColorSelect: (color?: string, image?: string) => void;
  zoomStyle: React.CSSProperties;
  handleMouseMove: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  handleMouseLeave: () => void;
}

export const ProductSlider: React.FC<ProductSliderProps> = ({
  imagens,
  highlightedImage,
  zoomStyle,
  handleColorSelect,
  handleMouseMove,
  handleMouseLeave,
}): JSX.Element => {
  const limitedImages = imagens.slice(0, 5);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [baseScale, setBaseScale] = useState(1);
  const [initialDistance, setInitialDistance] = useState(0);
  const [isPinching, setIsPinching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const index = limitedImages.findIndex((img) => img === highlightedImage);
    if (index !== -1) {
      setCurrentIndex(index);
    }
  }, [highlightedImage, limitedImages]);

  useEffect(() => {
    if (containerRef.current) {
      const scrollPosition = currentIndex * containerRef.current.offsetWidth;
      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }
  }, [currentIndex]);

  useEffect(() => {
    if (isFullscreen && fullscreenContainerRef.current && !isPinching) {
      fullscreenContainerRef.current.scrollLeft =
        fullscreenIndex * window.innerWidth;
    }
  }, [fullscreenIndex, isFullscreen, isPinching]);

  useEffect(() => {
    if (isFullscreen) {
      const scrollY = window.scrollY;

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isFullscreen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentIndex < limitedImages.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      handleColorSelect(undefined, limitedImages[newIndex]);
    }

    if (isRightSwipe && currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      handleColorSelect(undefined, limitedImages[newIndex]);
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const getDistance = (touch1: React.Touch, touch2: React.Touch) => {
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
    );
  };

  const handleImageTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      setIsPinching(true);
      const distance = getDistance(e.touches[0], e.touches[1]);
      setInitialDistance(distance);
      setBaseScale(scale);

      if (fullscreenContainerRef.current) {
        fullscreenContainerRef.current.style.overflowX = 'hidden';
      }
    } else if (e.touches.length === 1) {
      setIsPinching(false);
    }
  };

  const handleImageTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && isPinching && initialDistance > 0) {
      const currentDistance = getDistance(e.touches[0], e.touches[1]);
      const ratio = currentDistance / initialDistance;
      const newScale = Math.max(1, Math.min(4, baseScale * ratio));
      setScale(newScale);
    }
  };

  const handleImageTouchEnd = () => {
    setIsPinching(false);
    setInitialDistance(0);

    if (fullscreenContainerRef.current) {
      fullscreenContainerRef.current.style.overflowX = 'auto';
    }

    if (scale < 1.1) {
      setScale(1);
      setBaseScale(1);
    } else {
      setBaseScale(scale);
    }
  };

  const handleImageClick = (index: number) => {
    if (window.innerWidth < 768) {
      setFullscreenIndex(index);
      setIsFullscreen(true);
      setScale(1);
      setBaseScale(1);
      setInitialDistance(0);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    handleColorSelect(undefined, limitedImages[index]);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    setScale(1);
    setBaseScale(1);
    setInitialDistance(0);
  };

  const handleFullscreenScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isPinching) return;

    const container = e.currentTarget;
    const scrollLeft = container.scrollLeft;
    const itemWidth = window.innerWidth;
    const index = Math.round(scrollLeft / itemWidth);

    if (
      index !== fullscreenIndex &&
      index >= 0 &&
      index < limitedImages.length
    ) {
      setFullscreenIndex(index);
      setScale(1);
      setBaseScale(1);
    }
  };

  const FullscreenModal = () => {
    if (!isFullscreen) return null;

    return createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: '#000',
          zIndex: 999999,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              color: 'white',
              fontSize: '16px',
              fontWeight: '500',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '6px 12px',
              borderRadius: '9999px',
            }}
          >
            {fullscreenIndex + 1} / {limitedImages.length}
          </div>
          <button
            onClick={closeFullscreen}
            style={{
              color: 'white',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '9999px',
              border: 'none',
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div
          ref={fullscreenContainerRef}
          onScroll={handleFullscreenScroll}
          style={{
            width: '100%',
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: isPinching ? 'none' : 'x mandatory',
            display: 'flex',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {limitedImages.map((src, index) => (
            <div
              key={index}
              onTouchStart={handleImageTouchStart}
              onTouchMove={handleImageTouchMove}
              onTouchEnd={handleImageTouchEnd}
              style={{
                minWidth: '100vw',
                width: '100vw',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                scrollSnapAlign: 'center',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              <img
                src={src}
                alt={`Imagem ${index + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  transform:
                    index === fullscreenIndex ? `scale(${scale})` : 'scale(1)',
                  transition: scale === 1 ? 'transform 0.2s ease-out' : 'none',
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <div className="w-full flex flex-col-reverse items-center gap-2">
        <div
          className="hidden md:flex w-full flex gap-5 overflow-y-auto py-2 justify-center"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {limitedImages.map((src, index) => (
            <div
              key={index}
              className="h-20 w-20 flex items-center justify-center shadow-md bg-white rounded-md"
            >
              <img
                src={src}
                alt={`Thumbnail ${index + 1}`}
                className="object-contain w-full h-full rounded-md cursor-pointer md:hover:scale-105"
                onClick={() => handleColorSelect(undefined, src)}
                onMouseEnter={() => handleColorSelect(undefined, src)}
                onMouseLeave={() => handleColorSelect(undefined, src)}
              />
            </div>
          ))}
        </div>

        <div className="w-full relative">
          <div
            ref={containerRef}
            className="w-full overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="flex">
              {limitedImages.map((src, index) => (
                <div
                  key={index}
                  className="w-full h-[300px] md:h-[500px] flex-shrink-0 snap-center"
                  onClick={() => handleImageClick(index)}
                >
                  <div
                    className="w-full h-full flex items-center justify-center rounded-md bg-white"
                    onMouseMove={
                      index === currentIndex ? handleMouseMove : undefined
                    }
                    onMouseLeave={handleMouseLeave}
                    style={{
                      backgroundImage: `url(${src})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition:
                        index === currentIndex && zoomStyle.backgroundPosition
                          ? zoomStyle.backgroundPosition
                          : 'center',
                      backgroundSize:
                        index === currentIndex && zoomStyle.backgroundSize
                          ? zoomStyle.backgroundSize
                          : 'contain',
                      ...(index === currentIndex ? zoomStyle : {}),
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {limitedImages.length > 1 && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
              {limitedImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentIndex
                      ? 'w-6 bg-red-600'
                      : 'w-2 bg-red-600/40'
                  }`}
                  aria-label={`Ir para imagem ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <FullscreenModal />
    </>
  );
};
